import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  colorBackground,
  colorFieldsBorder,
  colorText,
  colorTextAdditional,
  colorAccent,
  colorHover,
  colorGray,
  colorLightBlue,
} from '../../../utils/palette';
import { borderRadius } from '../../../utils/styles';
import { KPI_REGISTRY, KPI_MAP } from '../kpis/kpiRegistry';
import {
  SectionLabel,
  SectionDesc,
  CountRow,
  CountLabel,
  CountBadge,
} from './customizerStyles';

const MAX_KPI = 5;

const DOMAIN_LABEL = {
  fuel: 'domain_fuel',
  utilization: 'domain_utilization',
  tco: 'domain_tco',
  oc: 'domain_oc',
  maintenance: 'domain_maintenance',
  workorders: 'domain_workorders',
};

// ── Section wrapper ────────────────────────────────────────────────────────────

const Section = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${colorFieldsBorder};
`;

// ── Slot list ──────────────────────────────────────────────────────────────────

const SlotList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilledSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${colorHover};
  border: 1px solid ${colorFieldsBorder};
  border-radius: ${borderRadius};
  padding: 10px 12px;
  position: relative;
  user-select: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    border-color: ${colorGray};
  }
`;

const DragHandle = styled.span`
  color: ${colorTextAdditional};
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
`;

const SlotInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SlotDomain = styled.div`
  font-size: 9px;
  font-weight: 400;
  color: ${colorTextAdditional};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 1px;
`;

const SlotName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${colorText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveButton = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: none;
  color: ${colorTextAdditional};
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
  font-family: inherit;

  &:hover {
    background: ${colorGray}22;
    color: ${colorGray};
  }
`;

// ── Add slot + picker ──────────────────────────────────────────────────────────

const AddSlotWrapper = styled.div`
  position: relative;
`;

const AddSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1.5px dashed ${({ $disabled }) => ($disabled ? colorFieldsBorder : colorGray)};
  border-radius: ${borderRadius};
  padding: 10px 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  color: ${({ $disabled }) => ($disabled ? colorFieldsBorder : colorTextAdditional)};
  font-size: 13px;
  font-weight: 500;
  transition: all 0.15s;

  ${({ $disabled }) =>
    !$disabled &&
    `
    &:hover {
      border-color: ${colorLightBlue};
      color: ${colorText};
      background: ${colorHover};
    }
  `}
`;

const Picker = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 200;
  background: ${colorBackground};
  border: 1px solid ${colorFieldsBorder};
  border-radius: ${borderRadius};
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  max-height: 260px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PickerSearch = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${colorFieldsBorder};
  flex-shrink: 0;
`;

const PickerSearchInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  font-size: 12px;
  font-family: inherit;
  color: ${colorText};
  background: transparent;

  &::placeholder {
    color: ${colorTextAdditional};
  }
`;

const PickerList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const PickerGroupLabel = styled.div`
  font-size: 9px;
  font-weight: 700;
  color: ${colorTextAdditional};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 12px 4px;
`;

const PickerItem = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  color: ${colorText};
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: ${colorHover};
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

export default function KpiSlotsSection({ draftKpiIds, setDraftKpiIds }) {
  const { t } = useTranslation('common');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const pickerRef = useRef(null);

  const atMax = draftKpiIds.length >= MAX_KPI;

  // Close picker on outside click
  useEffect(() => {
    function handleClick(e) {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
        setSearch('');
      }
    }
    if (pickerOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [pickerOpen]);

  function onDragEnd(result) {
    if (!result.destination) return;
    const next = Array.from(draftKpiIds);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setDraftKpiIds(next);
  }

  function removeKpi(id) {
    setDraftKpiIds(draftKpiIds.filter((k) => k !== id));
  }

  function addKpi(id) {
    if (atMax) return;
    setDraftKpiIds([...draftKpiIds, id]);
    // setPickerOpen(false);
    // setSearch('');
  }

  // KPIs available to add (not already active), grouped by domain
  const available = KPI_REGISTRY.filter((k) => !draftKpiIds.includes(k.id));
  const filtered = search
    ? available.filter((k) => t(k.labelKey, { defaultValue: k.label }).toLowerCase().includes(search.toLowerCase()))
    : available;

  const domains = [...new Set(KPI_REGISTRY.map((k) => k.domain))];
  const byDomain = domains.reduce((acc, d) => {
    acc[d] = filtered.filter((k) => k.domain === d);
    return acc;
  }, {});

  return (
    <Section>
      <SectionLabel>{t('kpi_bar')}</SectionLabel>
      <SectionDesc>{t('kpi_bar_description')}</SectionDesc>

      <CountRow>
        <CountLabel>{t('selected')}</CountLabel>
        <CountBadge $atMax={atMax}>
          {draftKpiIds.length} / {MAX_KPI}
        </CountBadge>
      </CountRow>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="kpi-slots" direction="vertical">
          {(provided) => (
            <SlotList ref={provided.innerRef} {...provided.droppableProps}>
              {draftKpiIds.map((id, index) => {
                const config = KPI_MAP[id];
                if (!config) return null;
                return (
                  <Draggable key={id} draggableId={id} index={index}>
                    {(draggableProvided) => (
                      <FilledSlot
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                      >
                        <DragHandle>⠿</DragHandle>
                        <SlotInfo>
                          <SlotDomain>{t(DOMAIN_LABEL[config.domain] ?? config.domain)}</SlotDomain>
                          <SlotName>{t(config.labelKey, { defaultValue: config.label })}</SlotName>
                        </SlotInfo>
                        <RemoveButton onClick={() => removeKpi(id)}>✕</RemoveButton>
                      </FilledSlot>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </SlotList>
          )}
        </Droppable>
      </DragDropContext>

      {!atMax && (
        <AddSlotWrapper ref={pickerRef} style={{ marginTop: 6 }}>
          <AddSlot onClick={() => setPickerOpen((o) => !o)}>
            + {t('add_kpi')}
          </AddSlot>

          {pickerOpen && (
            <Picker>
              <PickerSearch>
                <PickerSearchInput
                  autoFocus
                  placeholder={t('search_metrics')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </PickerSearch>
              <PickerList>
                {domains.map((domain) => {
                  const items = byDomain[domain];
                  if (!items || items.length === 0) return null;
                  return (
                    <div key={domain}>
                      <PickerGroupLabel>{t(DOMAIN_LABEL[domain] ?? domain)}</PickerGroupLabel>
                      {items.map((k) => (
                        <PickerItem key={k.id} onClick={() => addKpi(k.id)}>
                          {t(k.labelKey, { defaultValue: k.label })}
                        </PickerItem>
                      ))}
                    </div>
                  );
                })}
                {filtered.length === 0 && (
                  <PickerItem style={{ color: colorGray, cursor: 'default' }}>
                    {t('no_metrics_found')}
                  </PickerItem>
                )}
              </PickerList>
            </Picker>
          )}
        </AddSlotWrapper>
      )}
    </Section>
  );
}
